<template>
	<DropDownMenu
		ref="menuRef"
		is-icon
		type="light"
		aria-label="Analytics settings"
		position="right"
		wrapper-class="w-[300px] max-w-[calc(100vw-2rem)]"
		list-classes="!max-h-none !overflow-visible !py-0"
		@toggle="onToggle"
	>
		<template #toggle-button>
			<icon-base :width="16" :height="16" width-view-box="16" height-view-box="16" class="text-surface-8">
				<icon-gear />
			</icon-base>
		</template>
		<template #menu-items>
			<DropDownMenuItem is-custom>
				<div class="p-3" @click.stop>
					<div class="mb-2 text-xs font-medium text-surface-8">Timezone</div>
					<FormInput
						ref="searchRef"
						v-model="query"
						size="sm"
						placeholder="Search timezones"
						enterkeyhint="search"
						custom-classes="!bg-surface-3 !border-0"
					/>
					<ul class="mt-2 max-h-56 overflow-y-auto rounded-md bg-surface-3/50 scrollbar-thin scrollbar-thumb-surface-5 scrollbar-track-transparent" role="listbox" aria-label="Timezones">
						<li v-if="browserZone && matches(browserZone)">
							<button type="button" class="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-tiny hover:bg-surface-4" :class="modelValue === browserZone ? 'text-white' : 'text-surface-text-1'" @click="select(browserZone)">
								<span class="truncate">{{ browserZone }} <span class="text-surface-7">(browser)</span></span>
								<span class="shrink-0 text-xs text-surface-7 tabular-nums">{{ offsetLabel(browserZone) }}</span>
							</button>
						</li>
						<li v-for="tz in visibleZones" :key="tz">
							<button type="button" class="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-tiny hover:bg-surface-4" :class="modelValue === tz ? 'text-white' : 'text-surface-text-1'" role="option" :aria-selected="modelValue === tz" @click="select(tz)">
								<span class="truncate">{{ tz.replace(/_/g, ' ') }}</span>
								<span class="shrink-0 text-xs text-surface-7 tabular-nums">{{ offsetLabel(tz) }}</span>
							</button>
						</li>
						<li v-if="!visibleZones.length" class="px-3 py-3 text-center text-xs text-surface-7">No timezones match "{{ query }}"</li>
						<li v-else-if="filteredZones.length > visibleZones.length" class="px-3 py-1.5 text-center text-[11px] text-surface-6">
							{{ filteredZones.length - visibleZones.length }} more. Keep typing to narrow down.
						</li>
					</ul>
					<p class="mb-0 mt-2 text-[11px] leading-4 text-surface-6">Applies to all dates and times on this page. Saved on this device.</p>
				</div>
			</DropDownMenuItem>
		</template>
	</DropDownMenu>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconGear from '@/components/icon/IconGear.vue';
import { getSupportedTimeZones, getBrowserTimeZone, formatGmtOffset } from './analyticsTimezone';

const props = defineProps({
	modelValue: { type: String, required: true },
});
const emit = defineEmits(['update:modelValue']);

const MAX_VISIBLE = 80;
const menuRef = ref(null);
const searchRef = ref(null);
const query = ref('');
const browserZone = getBrowserTimeZone();
const zones = getSupportedTimeZones();

function matches(tz) {
	const q = query.value.trim().toLowerCase().replace(/\s+/g, '_');
	if (!q) return true;
	return tz.toLowerCase().includes(q) || tz.toLowerCase().replace(/_/g, ' ').includes(query.value.trim().toLowerCase());
}

const filteredZones = computed(() => zones.filter((tz) => tz !== browserZone && matches(tz)));
const visibleZones = computed(() => filteredZones.value.slice(0, MAX_VISIBLE));

function offsetLabel(tz) {
	try {
		return formatGmtOffset(tz);
	} catch (e) {
		return '';
	}
}

function select(tz) {
	if (tz !== props.modelValue) emit('update:modelValue', tz);
	menuRef.value?.hide?.();
}

async function onToggle(open) {
	if (!open) return;
	query.value = '';
	await nextTick();
	const input = searchRef.value?.$el?.querySelector?.('input');
	input?.focus?.();
}
</script>
